import { useEffect, useMemo, useRef, useState } from 'react';
import { levels } from '../data/defaultData';
import { supabase } from '../supabaseClient';

const WORDS_KEY = '__words';
const PATH_SEPARATOR = '\u001F';

function normalizeNode(node) {
  if (Array.isArray(node)) return { [WORDS_KEY]: node };
  return { ...(node || {}) };
}

function getWordsFromNode(node) {
  return Array.isArray(node) ? node : node?.[WORDS_KEY] || [];
}

function normalizeAnswer(value) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/å/g, 'a')
    .replace(/æ/g, 'ae')
    .replace(/ø/g, 'o')
    .replace(/[^\p{L}\p{N}]+/gu, '');
}

function normalizeText(value) {
  return String(value || '').trim().toLowerCase();
}

function escapeRegExp(value) {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function formatInfinitiveText(text) {
  const value = String(text || '').trim();
  if (!value) return '';
  return /^å\s+/i.test(value) ? value : `å ${value}`;
}

function isVerbCard(card) {
  const category = normalizeText(card.category);
  return card.word.type === 'verb' || ['глагол', 'verb', 'verbs'].includes(category);
}

function getNorwegianPrompt(card) {
  const forms = card.word.manualForms || card.word.forms || {};
  const base = forms.infinitive || card.word.text || '';

  return isVerbCard(card) && !normalizeText(base).startsWith('å ')
    ? `å ${base}`
    : base;
}

function getNorwegianAnswers(card) {
  const forms = card.word.manualForms || card.word.forms || {};
  return [
    card.word.text,
    getNorwegianPrompt(card),
    forms.infinitive,
    forms.present,
    forms.past,
    forms.perfect,
    forms.imperative,
  ]
    .filter(Boolean)
    .filter((answer, index, answers) => (
      answers.findIndex((candidate) => normalizeAnswer(candidate) === normalizeAnswer(answer)) === index
    ));
}

function getWordSearchTerms(card) {
  const word = card.word || {};
  const forms = {
    ...(word.forms || {}),
    ...(word.manualForms || {}),
  };

  return [
    word.text,
    isVerbCard(card) ? formatInfinitiveText(forms.infinitive || word.text) : '',
    ...Object.values(forms),
    forms.perfect ? `har ${forms.perfect}` : '',
  ]
    .filter(Boolean)
    .map((term) => normalizeText(term))
    .filter((term, index, terms) => term && terms.indexOf(term) === index);
}

function containsTerm(text, term) {
  const normalizedText = normalizeText(text);
  const normalizedTerm = normalizeText(term);

  if (!normalizedTerm) return false;
  if (normalizedTerm.includes(' ')) return normalizedText.includes(normalizedTerm);

  return new RegExp(`(^|[^\\p{L}])${escapeRegExp(normalizedTerm)}($|[^\\p{L}])`, 'u')
    .test(normalizedText);
}

function splitTextFragments(text) {
  return (String(text || '').match(/[^.!?\n]+[.!?]?/g) || [String(text || '')])
    .map((fragment) => fragment.trim())
    .filter(Boolean);
}

function getPhraseHints(card, savedTexts = []) {
  const terms = getWordSearchTerms(card);
  const hints = [];

  if (card?.word?.phrase) {
    hints.push({ type: 'Фраза', value: card.word.phrase, source: '' });
  }

  savedTexts.forEach((savedText) => {
    const fragments = splitTextFragments(savedText.text || '')
      .filter((fragment) => terms.some((term) => containsTerm(fragment, term)));

    fragments.forEach((fragment) => {
      if (hints.some((hint) => normalizeText(hint.value) === normalizeText(fragment))) return;

      hints.push({
        type: 'Фраза',
        value: fragment,
        source: savedText.id ? `tekst ${savedText.id}` : '',
      });
    });
  });

  return hints.slice(0, 3);
}

function answerWords(value) {
  return String(value || '')
    .split(/[\s,;/]+/)
    .map((w) => normalizeAnswer(w))
    .filter((w) => w.length >= 2);
}

function isAcceptedAnswer(input, acceptedAnswers) {
  const normalizedInput = normalizeAnswer(input);
  if (!normalizedInput) return false;
  const exact = acceptedAnswers.some((answer) => {
    const n = normalizeAnswer(answer);
    return n && normalizedInput === n;
  });
  if (exact) return true;
  const inputWords = new Set(answerWords(input));
  if (inputWords.size === 0) return false;
  return acceptedAnswers.some((answer) => (
    answerWords(answer).some((w) => inputWords.has(w))
  ));
}

function shuffle(items) {
  const nextItems = [...items];

  for (let index = nextItems.length - 1; index > 0; index -= 1) {
    const swapIndex = Math.floor(Math.random() * (index + 1));
    [nextItems[index], nextItems[swapIndex]] = [nextItems[swapIndex], nextItems[index]];
  }

  return nextItems;
}

function collectCardsFromNode({
  node,
  category,
  path = [],
  cards = [],
}) {
  const normalizedNode = normalizeNode(node);
  const pathLabel = [category, ...path].join(' / ');

  getWordsFromNode(normalizedNode).forEach((word, wordIndex) => {
    const translations = Array.isArray(word.translations)
      ? word.translations.map((item) => String(item || '').trim()).filter(Boolean)
      : [];

    if (translations.length === 0) return;

    cards.push({
      id: [category, ...path, word.text || wordIndex].join(PATH_SEPARATOR),
      category,
      subcategory: path.join(PATH_SEPARATOR),
      pathLabel,
      word,
      translations,
      level: word.lvl || 'A1',
    });
  });

  Object.entries(normalizedNode).forEach(([subcategory, childNode]) => {
    if (subcategory === WORDS_KEY) return;
    collectCardsFromNode({
      node: childNode,
      category,
      path: [...path, subcategory],
      cards,
    });
  });

  return cards;
}

function collectLearningCards(db) {
  return Object.entries(db || {}).flatMap(([category, node]) => (
    collectCardsFromNode({ node, category })
  ));
}

function collectSubscribedCards(subscribedDbs) {
  if (!Array.isArray(subscribedDbs)) return [];
  const result = [];
  subscribedDbs.forEach((entry) => {
    const author = entry?.nickname || 'другой пользователь';
    const authorId = entry?.author_id || '';
    const raw = (entry?.data && entry.data.db) ? entry.data.db : (entry?.data || {});
    collectLearningCards(raw).forEach((card) => {
      result.push({ ...card, id: 'sub:' + authorId + ':' + card.id, fromAuthor: author });
    });
  });
  return result;
}

function createEmptyProgress(progress) {
  return {
    learned: progress?.learned && typeof progress.learned === 'object' ? progress.learned : {},
    difficult: progress?.difficult && typeof progress.difficult === 'object' ? progress.difficult : {},
    reviewQueue: progress?.reviewQueue && typeof progress.reviewQueue === 'object' ? progress.reviewQueue : {},
    streak: progress?.streak && typeof progress.streak === 'object' ? progress.streak : {},
    totalSeen: Number(progress?.totalSeen) || 0,
  };
}

function getHintPool(card, hintTypes, savedTexts = []) {
  const hints = [];

  if (hintTypes.synonyms) {
    const list = (card.word.synonyms || []).filter(Boolean);
    if (list.length > 0) hints.push({ type: 'Синоним', value: list.join(', ') });
  }

  if (hintTypes.antonyms) {
    const list = (card.word.antonyms || []).filter(Boolean);
    if (list.length > 0) hints.push({ type: 'Антоним', value: list.join(', ') });
  }

  if (hintTypes.photo && card.word.photo?.dataUrl) {
    hints.push({ type: 'Фото', photo: card.word.photo.dataUrl });
  }

  if (hintTypes.phrase) {
    hints.push(...getPhraseHints(card, savedTexts));
  }

  return hints.slice(0, 3);
}

function getHintAvailability(card, savedTexts = []) {
  return {
    synonyms: Boolean(card?.word?.synonyms?.some(Boolean)),
    antonyms: Boolean(card?.word?.antonyms?.some(Boolean)),
    photo: Boolean(card?.word?.photo?.dataUrl),
    phrase: Boolean(card && getPhraseHints(card, savedTexts).length > 0),
  };
}

function getDefaultHintTypes(card, savedTexts = []) {
  const availability = getHintAvailability(card, savedTexts);

  return {
    synonyms: availability.synonyms,
    antonyms: !availability.synonyms && availability.antonyms,
    photo: !availability.synonyms && !availability.antonyms && availability.photo,
    phrase: !availability.synonyms && !availability.antonyms && !availability.photo && availability.phrase,
  };
}

function getHintTypesFromAvailability(availability) {
  return [
    availability.synonyms && { key: 'synonyms', label: 'Синоним' },
    availability.antonyms && { key: 'antonyms', label: 'Антоним' },
    availability.photo && { key: 'photo', label: 'Фото' },
    availability.phrase && { key: 'phrase', label: 'Фраза' },
  ].filter(Boolean);
}

function getBrowserName() {
  const userAgent = navigator.userAgent || '';

  if (userAgent.includes('OPR/') || userAgent.includes('Opera')) return 'Opera';
  if (userAgent.includes('Edg/')) return 'Edge';
  if (userAgent.includes('Chrome/')) return 'Chrome';
  return 'браузере';
}

function insertRandom(deck, card) {
  const nextDeck = [...deck];
  const index = Math.floor(Math.random() * (nextDeck.length + 1));
  nextDeck.splice(index, 0, card);
  return nextDeck;
}

function insertAfterTen(deck, card) {
  const nextDeck = [...deck];
  nextDeck.splice(Math.min(10, nextDeck.length), 0, card);
  return nextDeck;
}

function insertAt(deck, card, position) {
  const nextDeck = [...deck];
  nextDeck.splice(Math.min(position, nextDeck.length), 0, card);
  return nextDeck;
}

export default function WordLearningMode({
  db,
  categoryColors,
  savedTexts = [],
  learningProgress,
  onLearningProgressChange,
  onStudyActiveChange,
  deckSubcategories = [],
  onChangeDeckSubcategories,
  analyzerDeckCategory,
  onDeleteAnalyzerDeck,
  onClose,
  onResolveNounGender,
  onPersistNounGender,
}) {
  const progress = createEmptyProgress(learningProgress);
  const [subscribedCards, setSubscribedCards] = useState([]);
  const [publicRows, setPublicRows] = useState(null);
  const [audioMode, setAudioMode] = useState(false);
  const [includeSubscribed, setIncludeSubscribed] = useState(false);
  const [extraGenders, setExtraGenders] = useState({});
  const genderTriedRef = useRef(new Set());
  const ownCards = useMemo(() => collectLearningCards(db), [db]);
  const allCards = useMemo(() => {
    const merged = includeSubscribed ? [...ownCards, ...subscribedCards] : [...ownCards];
    if (!audioMode) return merged;
    return merged.map((card) => ({ ...card, id: card.id + ':audio' }));
  }, [ownCards, subscribedCards, audioMode, includeSubscribed]);
  const publicCards = useMemo(() => {
    const rows = Array.isArray(publicRows) ? publicRows : [];
    const cards = [];
    rows.forEach((r) => {
      const data = (r && r.data) || {};
      const ph = (data.photo && typeof data.photo === 'object') ? data.photo : null;
      const thumb = ph ? ph.thumb : null;
      if (!thumb) return;
      const translations = Array.isArray(data.translations)
        ? data.translations.map((t) => String(t || '').trim()).filter(Boolean)
        : [];
      if (translations.length === 0) return;
      const word = {
        text: r.word_text,
        translations,
        synonyms: Array.isArray(data.synonyms) ? data.synonyms : [],
        antonyms: Array.isArray(data.antonyms) ? data.antonyms : [],
        photo: { dataUrl: thumb, name: ph.name || '' },
        gender: data.gender || '',
        lvl: 'A1',
        type: data.type || data.pos || '',
        ord: { authored: true },
      };
      cards.push({
        id: 'pub:' + r.word_norm,
        category: r.owner_nick || 'Публичные',
        subcategory: '',
        pathLabel: 'Публичные слова',
        word,
        translations,
        level: 'A1',
      });
    });
    if (!audioMode) return cards;
    return cards.map((c) => ({ ...c, id: c.id + ':audio' }));
  }, [publicRows, audioMode]);
  const categories = useMemo(() => Object.keys(db || {}), [db]);
  const [allWords, setAllWords] = useState(true);
  const [publicOnly, setPublicOnly] = useState(false);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedDecks, setSelectedDecks] = useState([]);
  const [fullscreenPhoto, setFullscreenPhoto] = useState(null);
  const [removeDeckKey, setRemoveDeckKey] = useState(null);
  const [selectedLevels, setSelectedLevels] = useState(levels);
  const [direction, setDirection] = useState('no-ru');
  const [deckMode, setDeckMode] = useState('active');
  const [evalMode, setEvalMode] = useState('auto');
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [selectedHintType, setSelectedHintType] = useState(null);
  const [isHintMenuOpen, setIsHintMenuOpen] = useState(false);
  const [deck, setDeck] = useState([]);
  const [currentCard, setCurrentCard] = useState(null);
  const [answer, setAnswer] = useState('');
  const [result, setResult] = useState(null);
  const [nextDeckOverride, setNextDeckOverride] = useState(null);
  const [visibleHints, setVisibleHints] = useState([]);
  const [message, setMessage] = useState('');
  const [stats, setStats] = useState({ seen: 0, correct: 0, wrong: 0 });
  const [wrongMarked, setWrongMarked] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [voiceStatus, setVoiceStatus] = useState('');
  const recognitionRef = useRef(null);
  const voiceHeardRef = useRef(false);
  const voiceTranscriptRef = useRef('');
  const voiceBaseAnswerRef = useRef('');
  const voiceStartTimerRef = useRef(null);
  const voiceStopTimerRef = useRef(null);
  const SpeechRecognitionApi = typeof window !== 'undefined'
    ? window.SpeechRecognition || window.webkitSpeechRecognition
    : null;
  const speechSupported = Boolean(SpeechRecognitionApi);

  const deckCounters = useMemo(() => {
    const countSource = publicOnly ? publicCards : allCards;
    const learnedCount = countSource.filter((card) => progress.learned[card.id]).length;
    const waitingCount = countSource.filter((card) => {
      const delayedReview = progress.reviewQueue[card.id];
      return !progress.learned[card.id] && delayedReview?.dueAt > progress.totalSeen;
    }).length;

    return {
      active: Math.max(countSource.length - learnedCount - waitingCount, 0),
      learned: learnedCount,
      delayed: waitingCount,
      total: countSource.length,
    };
  }, [publicOnly, publicCards, allCards, progress.learned, progress.reviewQueue, progress.totalSeen]);

  const availableCards = useMemo(() => { const source = publicOnly ? publicCards : allCards; return source.filter((card) => {
    const isLearned = Boolean(progress.learned[card.id]);
    const delayedReview = progress.reviewQueue[card.id];
    const isWaitingForReview = delayedReview?.dueAt > progress.totalSeen;

    if (deckMode === 'learned' && !isLearned) return false;
    if (deckMode === 'active' && isLearned) return false;
    if (deckMode === 'active' && isWaitingForReview) return false;
    if (publicOnly) return true;
    if (!allWords) {
      const noCatDeck = selectedCategories.length === 0 && selectedDecks.length === 0;
      if (noCatDeck) {
        return false;
      } else {
        const cardDeckKey = card.category + PATH_SEPARATOR + (card.subcategory || '');
        const inCategory = selectedCategories.includes(card.category);
        const inDeck = selectedDecks.some((deckKey) => (
          cardDeckKey === deckKey || cardDeckKey.startsWith(deckKey + PATH_SEPARATOR)
        ));
        if (!inCategory && !inDeck) return false;
      }
    }
    if (selectedLevels.length > 0 && !selectedLevels.includes(card.level)) return false;
    return true;
  }); }, [
    publicOnly,
    publicCards,
    allCards,
    allWords,
    selectedCategories,
    selectedDecks,
    selectedLevels,
    deckMode,
    progress.learned,
    progress.reviewQueue,
    progress.totalSeen,
  ]);

  const hasSelection = allWords || publicOnly || selectedCategories.length > 0 || selectedDecks.length > 0;

  const delayedReviewCount = useMemo(() => (
    deckCounters.delayed
  ), [deckCounters.delayed]);

  const currentHintAvailability = currentCard ? getHintAvailability(currentCard, savedTexts) : {};
  const availableHintTypes = getHintTypesFromAvailability(currentHintAvailability);
  const totalHintsAvailable = currentCard
    ? getHintPool(currentCard, { synonyms: true, antonyms: true, photo: true, phrase: true }, savedTexts).length
    : 0;

  const resetCardState = () => {
    stopVoiceRecognition();
    setAnswer('');
    setResult(null);
    setNextDeckOverride(null);
    setVisibleHints([]);
    setWrongMarked(false);
    setSelectedHintType(null);
    setIsHintMenuOpen(false);
    setIsListening(false);
    setVoiceStatus('');
  };

  useEffect(() => {
    let mounted = true;
    supabase.rpc('get_subscribed_databases').then(({ data, error }) => {
      if (!mounted || error || !Array.isArray(data)) return;
      setSubscribedCards(collectSubscribedCards(data));
    }).catch(() => {});
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    if (!publicOnly || publicRows !== null) return undefined;
    let mounted = true;
    supabase
      .from('ordskjonnhet_public')
      .select('word_norm, word_text, owner_nick, data')
      .eq('authored', true)
      .limit(2000)
      .then(({ data, error }) => {
        if (!mounted) return;
        setPublicRows((error || !Array.isArray(data)) ? [] : data);
      })
      .catch(() => { if (mounted) setPublicRows([]); });
    return () => { mounted = false; };
  }, [publicOnly, publicRows]);



  useEffect(() => {
    if (onStudyActiveChange) onStudyActiveChange(Boolean(currentCard));
  }, [currentCard, onStudyActiveChange]);

  useEffect(() => () => {
    if (voiceStartTimerRef.current) {
      window.clearTimeout(voiceStartTimerRef.current);
    }
    if (voiceStopTimerRef.current) {
      window.clearTimeout(voiceStopTimerRef.current);
    }

    if (recognitionRef.current) {
      recognitionRef.current.stop();
    }
  }, []);

  const startTraining = () => {
    if (!hasSelection) {
      setMessage('Сначала выберите, что учить: все слова, категорию или колоду.');
      return;
    }
    const cards = shuffle(availableCards);

    if (cards.length === 0) {
      setCurrentCard(null);
      setDeck([]);
      setMessage('Нет карточек: у выбранных слов нет перевода на русский. Добавьте перевод в словаре, чтобы учить их по карточкам.');
      return;
    }

    setCurrentCard(cards[0]);
    setDeck(cards.slice(1));
    setStats({ seen: 1, correct: 0, wrong: 0 });
    setMessage('');
    setIsFilterOpen(false);
    resetCardState();
  };

  const exitTraining = () => {
    setCurrentCard(null);
    setDeck([]);
    setMessage('');
    resetCardState();
    setIsFilterOpen(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const moveToNext = (nextDeck = deck) => {
    onLearningProgressChange((currentProgress) => {
      const nextProgress = createEmptyProgress(currentProgress);
      return {
        ...nextProgress,
        totalSeen: nextProgress.totalSeen + 1,
      };
    });

    if (nextDeck.length === 0) {
      setCurrentCard(null);
      setDeck([]);
      setMessage('Тренировка закончена.');
      resetCardState();
      return;
    }

    const [nextCard, ...restDeck] = nextDeck;
    setCurrentCard(nextCard);
    setDeck(restDeck);
    setStats((current) => ({ ...current, seen: current.seen + 1 }));
    resetCardState();
  };

  const stopVoiceRecognition = () => {
    const rec = recognitionRef.current;
    if (rec) {
      rec.onend = null;
      rec.onresult = null;
      rec.onerror = null;
      try { rec.stop(); } catch { /* noop */ }
      try { rec.abort(); } catch { /* noop */ }
      recognitionRef.current = null;
    }
    if (voiceStopTimerRef.current) {
      window.clearTimeout(voiceStopTimerRef.current);
      voiceStopTimerRef.current = null;
    }
    if (voiceStartTimerRef.current) {
      window.clearTimeout(voiceStartTimerRef.current);
      voiceStartTimerRef.current = null;
    }
    setIsListening(false);
    setVoiceStatus('');
  };

  const checkAnswer = () => {
    if (!currentCard) return;
    stopVoiceRecognition();

    const acceptedAnswers = direction === 'no-ru'
      ? currentCard.translations
      : getNorwegianAnswers(currentCard);

    if (isAcceptedAnswer(answer, acceptedAnswers)) {
      setResult('correct');
      setVoiceStatus('');
      setStats((current) => ({ ...current, correct: current.correct + 1 }));
      return;
    }

    setResult('wrong');
    if (!wrongMarked) {
      setStats((current) => ({ ...current, wrong: current.wrong + 1 }));
      setWrongMarked(true);
    }
  };

  const toggleVoiceInput = async () => {
    if (!currentCard) return;

    if (!speechSupported) {
      const text = 'Голосовой ввод не поддерживается этим браузером. Попробуйте открыть сайт в Chrome или Edge.';
      setVoiceStatus(text);
      return;
    }

    if (isListening) {
      recognitionRef.current?.stop();
      setIsListening(false);
      setVoiceStatus('Голосовой ввод остановлен.');
      if (voiceStopTimerRef.current) {
        window.clearTimeout(voiceStopTimerRef.current);
        voiceStopTimerRef.current = null;
      }
      return;
    }

    const recognition = new SpeechRecognitionApi();

    setAnswer('');
    recognition.lang = direction === 'no-ru' ? 'ru-RU' : 'no-NO';
    recognition.interimResults = true;
    recognition.continuous = true;
    recognition.maxAlternatives = 1;
    voiceHeardRef.current = false;
    voiceTranscriptRef.current = '';
    voiceBaseAnswerRef.current = '';

    recognition.onstart = () => {
      if (voiceStartTimerRef.current) {
        window.clearTimeout(voiceStartTimerRef.current);
        voiceStartTimerRef.current = null;
      }

      setIsListening(true);
      const text = 'Слушаю';
      setVoiceStatus(text);
      voiceStopTimerRef.current = window.setTimeout(() => {
        try {
          recognition.stop();
        } catch {
          // SpeechRecognition can throw if it already stopped itself.
        }
      }, 10000);
    };

    recognition.onaudiostart = () => {
      setVoiceStatus('Слушаю');
    };

    recognition.onsoundstart = () => {
      setVoiceStatus('Слушаю');
    };

    recognition.onresult = (event) => {
      const transcript = Array.from(event.results || [])
        .slice(event.resultIndex || 0)
        .map((result) => result[0]?.transcript || '')
        .filter(Boolean)
        .join(' ')
        .trim();

      if (transcript) {
        voiceHeardRef.current = true;
        voiceTranscriptRef.current = transcript;
        setAnswer(
          voiceBaseAnswerRef.current
            ? `${voiceBaseAnswerRef.current} ${transcript}`
            : transcript,
        );
        setVoiceStatus(`Распознано: ${transcript}`);
        setMessage('');
      }
    };

    recognition.onspeechstart = () => {
      voiceHeardRef.current = true;
      setVoiceStatus('Слушаю');
    };

    recognition.onspeechend = () => {
      setVoiceStatus('Слушаю');
    };

    recognition.onnomatch = () => {
      setVoiceStatus('Речь услышал, но не смог распознать. Попробуйте еще раз.');
    };

    recognition.onsoundend = () => {
      setVoiceStatus((currentStatus) => (
        voiceTranscriptRef.current ? currentStatus : 'Слушаю'
      ));
    };

    recognition.onerror = (event) => {
      const errorMessage = event.error === 'not-allowed'
        ? 'Микрофон недоступен. Разрешите доступ к микрофону в браузере.'
        : 'Не получилось распознать голос. Попробуйте еще раз.';

      setVoiceStatus(errorMessage);
      setIsListening(false);
      if (voiceStartTimerRef.current) {
        window.clearTimeout(voiceStartTimerRef.current);
        voiceStartTimerRef.current = null;
      }
      if (voiceStopTimerRef.current) {
        window.clearTimeout(voiceStopTimerRef.current);
        voiceStopTimerRef.current = null;
      }
      setVoiceStatus(errorMessage);
    };

    recognition.onend = () => {
      setIsListening(false);
      if (voiceStartTimerRef.current) {
        window.clearTimeout(voiceStartTimerRef.current);
        voiceStartTimerRef.current = null;
      }
      if (!voiceTranscriptRef.current) {
        const browserName = getBrowserName();
        const browserHint = browserName === 'Opera'
          ? ' В Opera распознавание речи часто не возвращает текст; для голосового режима лучше открыть Chrome или Edge.'
          : '';

        setVoiceStatus(
          voiceHeardRef.current
            ? `Речь была услышана, но текст не распознался. Попробуйте говорить медленнее или проверьте язык карточки.${browserHint}`
            : `Голосовой ввод завершен без распознанного текста.${browserHint || ' Попробуйте еще раз или откройте Chrome/Edge.'}`,
        );
      } else {
        setVoiceStatus(`Распознано: ${voiceTranscriptRef.current}`);
      }
    };

    recognitionRef.current = recognition;

    try {
      setVoiceStatus('Слушаю');
      recognition.start();
      voiceStartTimerRef.current = window.setTimeout(() => {
        setIsListening(false);
        setVoiceStatus('Распознавание не стартовало. Чаще всего это ограничение браузера: попробуйте открыть сайт в обычном Chrome или Edge, проверьте интернет и разрешение микрофона.');
        try {
          recognition.stop();
        } catch {
          // SpeechRecognition can throw if the engine never started.
        }
      }, 3500);
    } catch {
      const text = 'Голосовой ввод уже запущен или браузер не дал его открыть.';
      setVoiceStatus(text);
      setIsListening(false);
    }
  };

  const showNextHint = (hintType = selectedHintType) => {
    const nextHintTypes = hintType
      ? { synonyms: false, antonyms: false, photo: false, [hintType]: true }
      : getDefaultHintTypes(currentCard, savedTexts);
    const nextHintPool = currentCard ? getHintPool(currentCard, nextHintTypes, savedTexts) : [];
    const alreadyVisibleOfType = visibleHints.filter((hint) => hint.key === hintType).length;
    const nextHint = nextHintPool[hintType ? alreadyVisibleOfType : visibleHints.length];

    if (!nextHint) {
      setMessage('Для этой карточки подсказок нет.');
      return;
    }

    setSelectedHintType(hintType || null);
    setVisibleHints((current) => [...current, { ...nextHint, key: hintType || nextHint.type }]);
    setIsHintMenuOpen(false);
    setMessage('');
  };

  const markCardAsDifficult = () => {
    if (!currentCard) return deck;

    onLearningProgressChange((currentProgress) => {
      const nextProgress = createEmptyProgress(currentProgress);
      const { [currentCard.id]: removedLearned, ...learned } = nextProgress.learned;
      return {
        ...nextProgress,
        learned,
        difficult: { ...nextProgress.difficult, [currentCard.id]: true },
        streak: { ...nextProgress.streak, [currentCard.id]: 0 },
        reviewQueue: {
          ...nextProgress.reviewQueue,
          [currentCard.id]: {
            dueAt: nextProgress.totalSeen + 10,
            updatedAt: new Date().toISOString(),
          },
        },
      };
    });

    return insertAfterTen(deck, currentCard);
  };

  const revealAnswer = () => {
    stopVoiceRecognition();
    const nextDeck = markCardAsDifficult();
    setNextDeckOverride(nextDeck);
    setResult('revealed');
    setStats((current) => ({
      ...current,
      wrong: wrongMarked ? current.wrong : current.wrong + 1,
    }));
    setWrongMarked(true);
  };


  const lightReveal = () => {
    if (!currentCard) return;
    setResult('revealed');
  };

  const lightNext = () => {
    if (!currentCard) return;
    const cycledDeck = [...deck, currentCard];
    const [nextCard, ...restDeck] = cycledDeck;
    setCurrentCard(nextCard);
    setDeck(restDeck);
    setStats((current) => ({ ...current, seen: current.seen + 1 }));
    resetCardState();
  };

  const autoAdvanceCorrect = () => {
    if (!currentCard) return;
    const usedHint = visibleHints.length > 0;
    const id = currentCard.id;
    if (usedHint) {
      onLearningProgressChange((cp) => {
        const np = createEmptyProgress(cp);
        return { ...np, streak: { ...np.streak, [id]: 0 } };
      });
      moveToNext(insertAt(deck, currentCard, 20));
      return;
    }
    let learnedNow = false;
    onLearningProgressChange((cp) => {
      const np = createEmptyProgress(cp);
      const next = (Number(np.streak[id]) || 0) + 1;
      if (next >= 4) {
        learnedNow = true;
        const { [id]: rl, ...streak } = np.streak;
        const { [id]: rd, ...difficult } = np.difficult;
        const { [id]: rq, ...reviewQueue } = np.reviewQueue;
        return { ...np, learned: { ...np.learned, [id]: true }, difficult, reviewQueue, streak };
      }
      return { ...np, streak: { ...np.streak, [id]: next } };
    });
    if (learnedNow) {
      setMessage('Слово выучено!');
      moveToNext(deck);
    } else {
      moveToNext(insertRandom(deck, currentCard));
    }
  };

  const rateCard = (rating) => {
    if (!currentCard) return;

    if (rating === 'learned') {
      onLearningProgressChange((currentProgress) => {
        const nextProgress = createEmptyProgress(currentProgress);
        const { [currentCard.id]: removed, ...difficult } = nextProgress.difficult;
        const { [currentCard.id]: removedReview, ...reviewQueue } = nextProgress.reviewQueue;
        return {
          ...nextProgress,
          learned: { ...nextProgress.learned, [currentCard.id]: true },
          difficult,
          reviewQueue,
        };
      });
      moveToNext(deck);
      return;
    }

    if (rating === 'again') {
      onLearningProgressChange((currentProgress) => {
        const nextProgress = createEmptyProgress(currentProgress);
        const { [currentCard.id]: removedLearned, ...learned } = nextProgress.learned;
        const { [currentCard.id]: removedDifficult, ...difficult } = nextProgress.difficult;
        const { [currentCard.id]: removedReview, ...reviewQueue } = nextProgress.reviewQueue;
        return {
          ...nextProgress,
          learned,
          difficult,
          reviewQueue,
        };
      });
      moveToNext(insertRandom(deck, currentCard));
      return;
    }

    moveToNext(markCardAsDifficult());
  };

  const toggleCategory = (category) => {
    setAllWords(false);
    setSelectedCategories((current) => (
      current.includes(category)
        ? current.filter((item) => item !== category)
        : [...current, category]
    ));
  };


  const toggleDeck = (deckKey) => {
    setAllWords(false);
    setSelectedDecks((current) => (
      current.includes(deckKey)
        ? current.filter((item) => item !== deckKey)
        : [...current, deckKey]
    ));
  };

  const removeDeck = (deckKey) => {
    setRemoveDeckKey(deckKey);
  };

  const isAnalyzerDeck = (deckKey) => Boolean(
    analyzerDeckCategory && deckKey && (
      deckKey === analyzerDeckCategory || deckKey.startsWith(analyzerDeckCategory + PATH_SEPARATOR)
    ),
  );
  const confirmRemoveDeck = () => {
    const deckKey = removeDeckKey;
    if (!deckKey) return;
    setSelectedDecks((current) => current.filter((item) => item !== deckKey));
    if (isAnalyzerDeck(deckKey) && onDeleteAnalyzerDeck) {
      onDeleteAnalyzerDeck(deckKey);
    } else if (onChangeDeckSubcategories) {
      onChangeDeckSubcategories((current) => (
        (Array.isArray(current) ? current : []).filter((item) => item !== deckKey)
      ));
    }
    setRemoveDeckKey(null);
  };

  const deckCardKeys = useMemo(() => (
    allCards.map((card) => card.category + PATH_SEPARATOR + (card.subcategory || ''))
  ), [allCards]);

  const deckCount = (deckKey) => (
    deckCardKeys.filter((key) => (
      key === deckKey || key.startsWith(deckKey + PATH_SEPARATOR)
    )).length
  );

  const deckLabel = (deckKey) => {
    const parts = String(deckKey).split(PATH_SEPARATOR).filter(Boolean);
    if (parts.length === 0) return deckKey;
    return parts[parts.length - 1];
  };

  const toggleLevel = (level) => {
    setSelectedLevels((current) => (
      current.includes(level)
        ? current.filter((item) => item !== level)
        : [...current, level]
    ));
  };

  const browserSpeak = (text, lang) => {
    if (!text || typeof window === 'undefined' || !window.speechSynthesis) return;
    try {
      window.speechSynthesis.cancel();
      const u = new window.SpeechSynthesisUtterance(String(text));
      u.lang = lang;
      const voices = window.speechSynthesis.getVoices();
      const v = voices.find((vo) => vo.lang && vo.lang.toLowerCase().startsWith(lang.slice(0, 2)));
      if (v) u.voice = v;
      u.rate = 0.92;
      window.speechSynthesis.speak(u);
    } catch { /* tts unavailable */ }
  };
  const speak = (text, lang) => {
    const t = (text == null ? '' : String(text)).trim();
    if (!t) return;
    const lc = (lang || '').toLowerCase();
    const isNorwegian = lc.indexOf('nb') === 0 || lc.indexOf('no') === 0;
    if (isNorwegian) {
      try {
        if (typeof window !== 'undefined' && window.speechSynthesis) window.speechSynthesis.cancel();
        const audio = new Audio('/tts/synth?text=' + encodeURIComponent(t));
        audio.play().catch(() => browserSpeak(t, lang));
        return;
      } catch (e) { /* fall back to browser tts */ }
    }
    browserSpeak(t, lang);
  };
  const promptLang = direction === 'no-ru' ? 'nb-NO' : 'ru-RU';
  const answerLang = direction === 'no-ru' ? 'ru-RU' : 'nb-NO';

  const promptText = currentCard
    ? direction === 'no-ru'
      ? getNorwegianPrompt(currentCard)
      : currentCard.translations[0]
    : '';
  const answerVariants = currentCard
    ? direction === 'no-ru'
      ? currentCard.translations
      : getNorwegianAnswers(currentCard)
    : [];
  const cardNounArticle = currentCard && currentCard.word && currentCard.word.type === 'noun'
    ? (currentCard.word.gender || extraGenders[normalizeText(currentCard.word.text)] || '')
    : '';
  const promptArticle = direction === 'no-ru' && cardNounArticle ? cardNounArticle + ' ' : '';
  const answerArticle = direction !== 'no-ru' && cardNounArticle ? cardNounArticle + ' ' : '';

  useEffect(() => {
    const w = currentCard && currentCard.word;
    if (!w || w.type !== 'noun' || w.gender || !onResolveNounGender) return;
    const t = normalizeText(w.text);
    if (!t || extraGenders[t] || genderTriedRef.current.has(t)) return;
    genderTriedRef.current.add(t);
    let cancelled = false;
    (async () => {
      try {
        const g = await onResolveNounGender(w.text);
        if (cancelled || !g) return;
        setExtraGenders((m) => ({ ...m, [t]: g }));
        if (onPersistNounGender) onPersistNounGender(currentCard.category, currentCard.subcategory, w.text, g);
      } catch (_) {}
    })();
    return () => { cancelled = true; };
  }, [currentCard, extraGenders, onResolveNounGender, onPersistNounGender]);

  return (
    <section className="learning-panel">
      {!currentCard && (
      <div className="learning-header">
        <div>
          <p className="learning-kicker">Карточки памяти</p>
          <h2>Учить слова</h2>
        </div>
      </div>
      )}

      {!currentCard && (<>
      <button
        className="learning-filter-toggle"
        type="button"
        onClick={() => setIsFilterOpen((value) => !value)}
      >
        Фильтры
      </button>

      {isFilterOpen && (
      <div className="learning-filter">
        <div className="learning-filter-block">
          <span>Колода</span>
          <div className="learning-deck-switch">
            <label className={deckMode === 'active' ? 'deck-opt active' : 'deck-opt'}>
              <input type="checkbox" checked={deckMode === 'active'} onChange={() => setDeckMode('active')} />
              <span className="deck-opt-label">Обычная</span>
            </label>
            <label className={deckMode === 'learned' ? 'deck-opt active' : 'deck-opt'}>
              <span className="deck-opt-label">Выученные</span>
              <input type="checkbox" checked={deckMode === 'learned'} onChange={() => setDeckMode('learned')} />
            </label>
          </div>
        </div>

        <label className="learning-checkbox primary">
          <input
            type="checkbox"
            checked={allWords}
            onChange={(event) => {
              const on = event.target.checked;
              setAllWords(on);
              if (on) {
                setPublicOnly(false);
                setSelectedCategories([]);
                setSelectedDecks([]);
              }
            }}
          />
          Учить все слова и фразы
        </label>

        <label className="learning-checkbox">
          <input
            type="checkbox"
            checked={publicOnly}
            onChange={(event) => {
              const on = event.target.checked;
              setPublicOnly(on);
              if (on) {
                setAllWords(false);
                setSelectedCategories([]);
                setSelectedDecks([]);
              }
            }}
          />
          Учить публичные слова
        </label>

        <label className="learning-checkbox">
          <input
            type="checkbox"
            checked={includeSubscribed}
            onChange={(event) => setIncludeSubscribed(event.target.checked)}
          />
          Слова по подпискам
        </label>

        <div className="learning-filter-block">
          <span>Режим оценки</span>
          <div className="learning-segment">
            <button type="button" className={evalMode === 'auto' ? 'active' : ''} onClick={() => setEvalMode('auto')}>
              Авто
            </button>
            <button type="button" className={evalMode === 'manual' ? 'active' : ''} onClick={() => setEvalMode('manual')}>
              Сам
            </button>
            <button type="button" className={evalMode === 'light' ? 'active' : ''} onClick={() => setEvalMode('light')}>
              Лайт
            </button>
          </div>
        </div>

        <label className="learning-checkbox primary">
          <input type="checkbox" checked={audioMode} onChange={(e) => setAudioMode(e.target.checked)} />
          Аудирование: слушать слово вместо текста
        </label>

        {deckSubcategories.length > 0 && (
          <div className="learning-filter-block">
            <span>Колоды</span>
            <div className="learning-chip-list">
              {deckSubcategories.map((deckKey) => (
                <span key={deckKey} className="learning-chip deck-chip">
                  <label className="deck-chip-label">
                    <input
                      type="checkbox"
                      checked={selectedDecks.includes(deckKey)}
                      onChange={() => toggleDeck(deckKey)}
                    />
                    {deckLabel(deckKey)}
                    <span className="deck-chip-count">{deckCount(deckKey)}</span>
                  </label>
                  <button
                    type="button"
                    className="deck-chip-remove"
                    onClick={() => removeDeck(deckKey)}
                    aria-label="Убрать колоду"
                    title="Убрать из колод"
                  >×</button>
                </span>
              ))}
            </div>
          </div>
        )}

      </div>
      )}

      <div className="learning-filter-block learning-direction-row">
        <span>Карточка показывает</span>
        <div className="learning-segment">
          <button
            type="button"
            className={direction === 'no-ru' ? 'active' : ''}
            onClick={() => setDirection('no-ru')}
          >
            norsk
          </button>
          <button
            type="button"
            className={direction === 'ru-no' ? 'active' : ''}
            onClick={() => setDirection('ru-no')}
          >
            русский
          </button>
        </div>
      </div>

      <div className="learning-start-row">
        <span>
          Подходит карточек: {availableCards.length}
          {` · всего: ${deckCounters.total}`}
          {delayedReviewCount > 0 && ` · для повтора: ${delayedReviewCount}`}
        </span>
        <button
          type="button"
          className="learning-start-button"
          onClick={startTraining}
          disabled={!hasSelection}
        >
          Учить
        </button>
      </div>
      {!hasSelection && (
        <p className="learning-select-hint">
          Выберите «Учить все слова и фразы», категорию или колоду, чтобы начать.
        </p>
      )}
      </>)}

      {currentCard && (
        <div className="learning-card learning-study-screen">
          <div className="learning-card-meta">
            <span>{currentCard.pathLabel}</span>
            <span>{currentCard.level}</span>
          </div>
          <div className="learning-prompt">
            {audioMode && promptLang === 'nb-NO' ? (
              <>
                {result && <span className="audio-word">{promptArticle}{promptText}</span>}
                <button type="button" className="speak-btn audio-only audio-corner" title="Прослушать слово" onClick={() => speak(promptArticle + promptText, promptLang)}>🔊</button>
              </>
            ) : (
              <>
                {promptLang === 'nb-NO' && (
                  <button type="button" className="speak-btn speak-btn-top" title="Озвучить" onClick={() => speak(promptArticle + promptText, promptLang)}>🔊</button>
                )}
                <span className="prompt-word">{promptArticle}{promptText}</span>
              </>
            )}
          </div>
          {currentCard.fromAuthor && (
            <div className="learning-from-author">от {currentCard.fromAuthor}</div>
          )}

          <div className="learning-answer-row">
            {evalMode !== 'light' && (
            <input
              type="text"
              value={answer}
              onChange={(event) => setAnswer(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') checkAnswer();
              }}
              placeholder="Введите ответ..."
            />
            )}
            <div className="study-actions-row">
            <button type="button" className="study-action" onClick={evalMode === 'light' ? lightReveal : checkAnswer} title="Проверить">
              <img src="/icons/proverka.png" alt="" className="study-action-icon" />
              <span>Проверить</span>
            </button>
            <button
              type="button"
              className={`study-action hint-button ${totalHintsAvailable > 0 ? 'has-hint' : ''}`}
              onClick={() => setIsHintMenuOpen((value) => !value)}
              disabled={availableHintTypes.length === 0 || visibleHints.length >= 3}
              title="Подсказка"
            >
              <img src="/icons/podskazka.png" alt="" className="study-action-icon" />
              <span>{totalHintsAvailable === 0 ? 'Нет подсказки' : `Подсказка (${Math.max(totalHintsAvailable - visibleHints.length, 0)})`}</span>
            </button>
            {evalMode !== 'light' && (<>
            <button type="button" className="study-action dont-know-button" onClick={revealAnswer} title="Не знаю">
              <img src="/icons/neznayu.png" alt="" className="study-action-icon" />
              <span>Не знаю</span>
            </button>
            <button
              type="button"
              className={`study-action voice-answer-button ${isListening ? 'listening' : ''}`}
              onClick={toggleVoiceInput}
              disabled={result === 'correct' || result === 'revealed'}
              title={direction === 'no-ru' ? 'Ответ голосом по-русски' : 'Svar med norsk tale'}
            >
              <img src="/icons/golos.png" alt="" className="study-action-icon" />
              <span>{isListening ? 'Слушаю...' : 'Голос'}</span>
            </button>
            </>)}
            </div>
          </div>

          <button type="button" className="learning-exit-link" onClick={exitTraining}>
            Выйти из обучающего режима
          </button>

          {voiceStatus && (
            <div className={`voice-status ${isListening ? 'listening' : ''}`}>
              {voiceStatus}
            </div>
          )}

          {isHintMenuOpen && (
            <div className="learning-hint-menu">
              {availableHintTypes.length > 0 ? (
                availableHintTypes.map((hintType) => {
                  const hintPool = getHintPool(currentCard, {
                    synonyms: false,
                    antonyms: false,
                    photo: false,
                    phrase: false,
                    [hintType.key]: true,
                  }, savedTexts);
                  const visibleCount = visibleHints.filter((hint) => hint.key === hintType.key).length;
                  const isUsedUp = visibleHints.length >= 3 || visibleCount >= hintPool.length;

                  return (
                    <button
                      key={hintType.key}
                      type="button"
                      disabled={isUsedUp}
                      onClick={() => showNextHint(hintType.key)}
                    >
                      {hintType.label}
                      <span>{Math.max(hintPool.length - visibleCount, 0)}</span>
                    </button>
                  );
                })
              ) : (
                <span>Нет доступных подсказок</span>
              )}
            </div>
          )}

          {visibleHints.length > 0 && (
            <div className="learning-hints">
              {visibleHints.map((hint, index) => (
                <div className={`learning-hint ${hint.type === 'Фраза' ? 'learning-hint-phrase' : ''}`} key={`${hint.type}-${index}`}>
                  <div className="learning-hint-head">
                    <strong>{hint.type}</strong>
                    {hint.source && <em>{hint.source}</em>}
                  </div>
                  {hint.photo ? (
                    <img
                      src={hint.photo}
                      alt="Подсказка"
                      className="hint-photo-clickable"
                      onClick={() => setFullscreenPhoto(hint.photo)}
                    />
                  ) : (
                    <span className="learning-hint-value">{hint.value}</span>
                  )}
                </div>
              ))}
            </div>
          )}

          {result === 'wrong' && (
            <p className="learning-result wrong">Скорее всего вы ошиблись, попробуйте еще раз</p>
          )}

          {result === 'correct' && (
            <div className="learning-result correct">
              <p>Верно.</p>
              <div>
                <span>{answerArticle}{answerVariants.join(', ')}</span>
                {answerLang === 'nb-NO' && (
                  <button type="button" className="speak-btn" title="Озвучить" onClick={() => speak(answerArticle + answerVariants[0], answerLang)}>🔊</button>
                )}
              </div>
              {evalMode === 'manual' && (
              <div className="memory-rating rating-icons">
                <button type="button" className="rating-icon-btn" onClick={() => rateCard('learned')} title="Хорошо, больше не учить">
                  <img src="/icons/choroshoneuchit.png" alt="" className="rating-icon" />
                  <span>Больше не учить</span>
                </button>
                <button type="button" className="rating-icon-btn" onClick={() => rateCard('again')} title="Хорошо, учить еще">
                  <img src="/icons/uchiteshe.png" alt="" className="rating-icon" />
                  <span>Учить ещё</span>
                </button>
                <button type="button" className="rating-icon-btn" onClick={() => rateCard('difficult')} title="Плохо, учить постоянно">
                  <img src="/icons/plocho.png" alt="" className="rating-icon" />
                  <span>Учить постоянно</span>
                </button>
              </div>
              )}
              {evalMode === 'auto' && (
              <div className="memory-rating single-action">
                <button type="button" onClick={autoAdvanceCorrect}>
                  Далее
                </button>
              </div>
              )}
            </div>
          )}

          {result === 'revealed' && (
            <div className={`learning-result revealed ${evalMode === 'light' ? 'learning-result-light' : ''}`}>
              <p>Ответ:</p>
              <div>
                <span>{answerArticle}{answerVariants.join(', ')}</span>
                {answerLang === 'nb-NO' && (
                  <button type="button" className="speak-btn" title="Озвучить" onClick={() => speak(answerArticle + answerVariants[0], answerLang)}>🔊</button>
                )}
              </div>
              {evalMode !== 'light' && (
              <div className="memory-rating single-action">
                <button type="button" onClick={() => moveToNext(nextDeckOverride || deck)}>
                  Продолжить
                </button>
              </div>
              )}
              {evalMode === 'light' && (
              <div className="memory-rating single-action">
                <button type="button" onClick={lightNext}>
                  Дальше
                </button>
              </div>
              )}
            </div>
          )}

          <div className="learning-progress">
            <span>Показано: {stats.seen}</span>
            <span>Верно: {stats.correct}</span>
            <span>Ошибок: {stats.wrong}</span>
            <span>В очереди: {deck.length}</span>
            <span>Память: {progress.totalSeen}</span>
          </div>
        </div>
      )}

      {fullscreenPhoto && (
        <div className="photo-fullscreen-overlay" onClick={() => setFullscreenPhoto(null)}>
          <button
            type="button"
            className="photo-fullscreen-close"
            onClick={() => setFullscreenPhoto(null)}
            aria-label="Закрыть"
          >×</button>
          <img src={fullscreenPhoto} alt="Фото" className="photo-fullscreen-img" onClick={(e) => e.stopPropagation()} />
        </div>
      )}
      {removeDeckKey && (
        <div className="deck-confirm-overlay" onClick={() => setRemoveDeckKey(null)}>
          <div className="deck-confirm-box" onClick={(e) => e.stopPropagation()}>
            <p className="deck-confirm-text">{isAnalyzerDeck(removeDeckKey) ? 'Удалить эту колоду вместе со словами? Это действие нельзя отменить.' : 'Убрать эту колоду из списка изучаемых?'}</p>
            <div className="deck-confirm-actions">
              <button type="button" className="deck-confirm-yes" onClick={confirmRemoveDeck}>Да</button>
              <button type="button" className="deck-confirm-no" onClick={() => setRemoveDeckKey(null)}>Нет</button>
            </div>
          </div>
        </div>
      )}
      {message && <p className="learning-message">{message}</p>}
    </section>
  );
}
